import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  Renderer2,
} from '@angular/core';
import { PermissionService } from '../../services/permission.service';
import { ScreenType } from '../../enums/screen.type';
import { AuthService } from '../../services/auth.service';

@Directive({
  selector: '[hasFeature]',
  standalone: false,
})
export class HasFeatureDirective {
  private permissionKey!: string;
  private permissionScreen!: ScreenType;
  private clientId!: string;
  private roleName!: string;
  private action!: 'disable' | 'watermark' | 'remove';
  private view: any;
  private currentContainer: HTMLElement | null = null;
  private originalStyles: Map<string, string> = new Map();

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService,
    private renderer: Renderer2,
    private authService: AuthService,
  ) {
    // Re-render when permissions arrive async
    this.permissionService.getPermission().subscribe((res) => {
      res && this.renderElement(this.action);
    });
  }

  @Input() set hasFeature(value: {
    screen: ScreenType;
    key: string;
    action: 'disable' | 'watermark' | 'remove';
    clientId?: string;
    roleName?: string;
  }) {
    this.permissionScreen = value.screen;
    this.permissionKey = value.key;
    this.action = value.action;
    this.clientId = value.clientId!;
    this.roleName = value.roleName!;
    this.renderElement(this.action);
  }

  /** Main render logic */
  private renderElement(action: 'disable' | 'watermark' | 'remove') {
    const permissions = this.permissionService.getItem();

    // Clean up previous container before clearing view
    if (this.currentContainer && this.currentContainer.parentNode) {
      this.cleanupContainer(this.currentContainer);
    }

    // If no permissions loaded yet, hide element temporarily
    if (!permissions) {
      this.viewContainer.clear();
      return;
    }

    var hasPermission = false;

    hasPermission = this.authService.isAdmin();

    if (!hasPermission && this.clientId) {
      hasPermission = this.permissionService.hasPermissionByClient(
        this.clientId,
        this.permissionScreen,
        this.permissionKey,
        this.roleName,
      );
    }

    // Check if user has permission
    if (!hasPermission) {
      hasPermission = Object.keys(permissions).some(
        (p) =>
          (permissions as any)[p]?.[this.permissionScreen]?.[
            this.permissionKey
          ],
      );
    }

    // Clear any existing view
    this.viewContainer.clear();

    if (hasPermission) {
      // User has permission - render normally
      this.view = this.viewContainer.createEmbeddedView(this.templateRef);
      const el: HTMLElement = this.view.rootNodes[0];
      this.enableElement(el);
    } else {
      // User doesn't have permission - apply action
      switch (action) {
        case 'disable':
          this.view = this.viewContainer.createEmbeddedView(this.templateRef);
          const disabledEl: HTMLElement = this.view.rootNodes[0];
          this.disableElement(disabledEl, this.permissionKey);
          break;
        case 'remove':
          // Don't render anything - element is hidden
          break;
        case 'watermark':
          this.view = this.viewContainer.createEmbeddedView(this.templateRef);
          const watermarkedEl: HTMLElement = this.view.rootNodes[0];
          this.watermarkElement(watermarkedEl, this.permissionKey);
          break;
      }
    }
  }

  /** Disable element - makes it non-interactive but visible */
  private disableElement(el: HTMLElement, feature: string) {
    if (!el) return;

    // Store original styles
    this.storeOriginalStyles(el, [
      'opacity',
      'cursor',
      'pointer-events',
      'user-select',
    ]);

    // Apply disabled styles
    this.renderer.setStyle(el, 'opacity', '0.4');
    this.renderer.setStyle(el, 'cursor', 'not-allowed');
    this.renderer.setStyle(el, 'pointer-events', 'none');
    this.renderer.setStyle(el, 'user-select', 'none');
    this.renderer.setAttribute(el, 'disabled', 'true');
    this.renderer.setAttribute(el, 'aria-disabled', 'true');
    this.renderer.setAttribute(
      el,
      'title',
      `This feature is disabled: ${feature}`,
    );
    // If it's a form element, disable it properly
    if (this.isFormElement(el)) {
      this.renderer.setProperty(el, 'disabled', true);
    }
    // Disable all child buttons and inputs
    this.disableChildElements(el);
  }

  /** Add watermark overlay without completely disabling */
  private watermarkElement(el: HTMLElement, feature: string) {
    if (!el) return;

    // If element is already in a permission wrapper, skip
    const existingWrapper = el.parentElement;
    if (existingWrapper?.classList.contains('permission-wrapper')) {
      this.currentContainer = existingWrapper;
      return;
    }

    // Clean up any existing watermark first
    this.cleanupWatermark(el);

    // Store original styles
    this.storeOriginalStyles(el, ['opacity', 'cursor', 'pointer-events']);

    // Apply semi-disabled styles
    this.renderer.setStyle(el, 'opacity', '0.3');
    this.renderer.setStyle(el, 'cursor', 'not-allowed');
    this.renderer.setStyle(el, 'pointer-events', 'none');

    const isButton = el.tagName.toLowerCase() === 'button';
    const watermarkText = isButton
      ? 'No Access'
      : `You are not authorized to access the ${feature}`;

    // Create watermark overlay
    const watermark = this.renderer.createElement('div');
    this.renderer.addClass(watermark, 'permission-watermark');
    this.renderer.setStyle(watermark, 'position', 'absolute');
    this.renderer.setStyle(watermark, 'top', '50%');
    this.renderer.setStyle(watermark, 'left', '50%');
    this.renderer.setStyle(watermark, 'transform', 'translate(-50%, -50%)');
    this.renderer.setStyle(watermark, 'color', '#d9534f');
    this.renderer.setStyle(watermark, 'font-size', isButton ? '10px' : '16px');
    this.renderer.setStyle(watermark, 'font-weight', '600');
    this.renderer.setStyle(watermark, 'pointer-events', 'none');
    this.renderer.setStyle(watermark, 'white-space', 'nowrap');
    this.renderer.setStyle(watermark, 'z-index', '1000');
    this.renderer.setStyle(watermark, 'text-align', 'center');
    this.renderer.setStyle(watermark, 'padding', '4px 8px');
    this.renderer.setStyle(watermark, 'background', '#000');
    this.renderer.setStyle(watermark, 'border-radius', '4px');
    this.renderer.setStyle(
      watermark,
      'box-shadow',
      '0 2px 4px rgba(0,0,0,0.1)',
    );
    this.renderer.setAttribute(watermark, 'data-watermark', 'true');

    const text = this.renderer.createText(watermarkText);
    this.renderer.appendChild(watermark, text);

    // Create wrapper container
    const container = this.renderer.createElement('div');
    this.renderer.addClass(container, 'permission-wrapper');
    this.renderer.setStyle(container, 'position', 'relative');
    this.renderer.setStyle(container, 'display', 'inline-block');
    this.renderer.setStyle(container, 'width', isButton ? 'auto' : '100%');

    // Insert wrapper and move element into it
    const parent = el.parentNode;
    if (parent) {
      parent.insertBefore(container, el);
      this.renderer.appendChild(container, el);
      this.renderer.appendChild(container, watermark);
    }

    // Store reference to current container
    this.currentContainer = container;
  }

  /** Enable element - restore to normal state */
  private enableElement(el: HTMLElement) {
    if (!el) return;

    // Restore original styles
    this.restoreOriginalStyles(el);

    // Remove disabled attributes
    this.renderer.removeAttribute(el, 'disabled');
    this.renderer.removeAttribute(el, 'aria-disabled');
    this.renderer.removeAttribute(el, 'title');

    // If it's a form element, enable it
    if (this.isFormElement(el)) {
      this.renderer.setProperty(el, 'disabled', false);
    }

    // Enable all child elements
    this.enableChildElements(el);

    // Clean up any watermark wrapper
    this.cleanupWatermark(el);
    this.currentContainer = null;
  }

  /** Store original styles before modification */
  private storeOriginalStyles(el: HTMLElement, styleProps: string[]) {
    styleProps.forEach((prop) => {
      const value = el.style.getPropertyValue(prop);
      if (value) {
        this.originalStyles.set(`${el.id || 'el'}-${prop}`, value);
      }
    });
  }

  /** Restore original styles */
  private restoreOriginalStyles(el: HTMLElement) {
    const stylesToRestore = [
      'opacity',
      'cursor',
      'pointer-events',
      'user-select',
    ];

    stylesToRestore.forEach((prop) => {
      const key = `${el.id || 'el'}-${prop}`;
      const originalValue = this.originalStyles.get(key);

      if (originalValue) {
        this.renderer.setStyle(el, prop, originalValue);
        this.originalStyles.delete(key);
      } else {
        this.renderer.removeStyle(el, prop);
      }
    });
  }

  /** Disable all child form elements */
  private disableChildElements(el: HTMLElement) {
    const formElements = el.querySelectorAll(
      'button, input, select, textarea, a',
    );
    formElements.forEach((child) => {
      this.renderer.setAttribute(child, 'disabled', 'true');
      this.renderer.setStyle(child, 'pointer-events', 'none');
      if (this.isFormElement(child as HTMLElement)) {
        this.renderer.setProperty(child, 'disabled', true);
      }
    });
  }

  /** Enable all child form elements */
  private enableChildElements(el: HTMLElement) {
    const formElements = el.querySelectorAll(
      'button, input, select, textarea, a',
    );
    formElements.forEach((child) => {
      this.renderer.removeAttribute(child, 'disabled');
      this.renderer.removeStyle(child, 'pointer-events');
      if (this.isFormElement(child as HTMLElement)) {
        this.renderer.setProperty(child, 'disabled', false);
      }
    });
  }

  /** Check if element is a form element */
  private isFormElement(el: HTMLElement): boolean {
    const formTags = ['button', 'input', 'select', 'textarea'];
    return formTags.includes(el.tagName.toLowerCase());
  }

  /** Clean up watermark and unwrap element */
  private cleanupWatermark(el: HTMLElement) {
    const wrapper = el.parentElement;

    if (wrapper?.classList.contains('permission-wrapper')) {
      // Remove all watermarks
      const watermarks = wrapper.querySelectorAll('[data-watermark="true"]');
      watermarks.forEach((watermark) => {
        this.renderer.removeChild(wrapper, watermark);
      });

      // Unwrap element
      const grandParent = wrapper.parentNode;
      if (grandParent) {
        grandParent.insertBefore(el, wrapper);
        this.renderer.removeChild(grandParent, wrapper);
      }
    }
  }

  /** Clean up a container and all its watermarks */
  private cleanupContainer(container: HTMLElement) {
    if (!container || !container.classList.contains('permission-wrapper')) {
      return;
    }

    // Remove all watermarks from container
    const watermarks = container.querySelectorAll('[data-watermark="true"]');
    watermarks.forEach((watermark) => {
      this.renderer.removeChild(container, watermark);
    });

    // Unwrap the element
    const element = container.querySelector(
      ':not([data-watermark="true"])',
    ) as HTMLElement;
    if (element && container.parentNode) {
      // Restore element styles before unwrapping
      this.restoreOriginalStyles(element);
      container.parentNode.insertBefore(element, container);
      this.renderer.removeChild(container.parentNode, container);
    }
  }

  /** Cleanup on directive destroy */
  ngOnDestroy() {
    if (this.currentContainer && this.currentContainer.parentNode) {
      this.cleanupContainer(this.currentContainer);
    }
    this.originalStyles.clear();
  }
}
